import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";

export type CookieConsent = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  region: "EU" | "US-CA" | "OTHER";
  timestamp: string;
};

const STORAGE_KEY = "iqt_cookie_consent_v1";

type Ctx = {
  consent: CookieConsent | null;
  isOpen: boolean;
  updateConsent: (next: { analytics: boolean; marketing: boolean; region?: CookieConsent["region"] }) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
};

const CookieConsentContext = createContext<Ctx | undefined>(undefined);

function detectRegion(): CookieConsent["region"] {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.startsWith("Europe/")) return "EU";
    if (tz === "America/Los_Angeles") return "US-CA";
  } catch {}
  return "OTHER";
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setConsent(JSON.parse(raw));
    } catch {}
    setLoaded(true);
  }, []);

  const persist = useCallback((next: CookieConsent) => {
    setConsent(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  }, []);

  const updateConsent: Ctx["updateConsent"] = (next) => {
    persist({
      essential: true,
      analytics: !!next.analytics,
      marketing: !!next.marketing,
      region: (next as any).region || consent?.region || detectRegion(),
      timestamp: new Date().toISOString(),
    });
    setIsOpen(false);
  };

  const acceptAll = () => persist({
    essential: true, analytics: true, marketing: true,
    region: consent?.region || detectRegion(), timestamp: new Date().toISOString(),
  });

  const rejectAll = () => persist({
    essential: true, analytics: false, marketing: false,
    region: consent?.region || detectRegion(), timestamp: new Date().toISOString(),
  });

  const value: Ctx = {
    consent: loaded ? consent : null,
    isOpen,
    updateConsent,
    acceptAll,
    rejectAll,
    openPreferences: () => setIsOpen(true),
    closePreferences: () => setIsOpen(false),
  };

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used within CookieConsentProvider");
  return ctx;
}
